/**
 * useReferralDashboardState — les gestes de la page : copier, partager, déplier les conditions, fermer.
 *
 * Pas de `useReducer` ici, et c'est voulu : le seul état d'interface de la page est le repli des
 * conditions, qui appartient à `<ReferralTermsCard>` (règle « l'état d'interface reste dans le
 * composant »). Le hook ne porte donc que les effets et leurs traces Amplitude.
 *
 * Le partage n'est pas réécrit : `useReferralShareAdapter` possède déjà les trois canaux, leurs
 * adresses, leurs messages d'échec et leurs événements — ce sont exactement ceux de
 * `src/admin/SocialShareButtons.js`, que la page legacy monte. On lui passe la charge utile déjà
 * chargée, donc il ne rappelle rien.
 */
import { useCallback } from "react"
import { useNavigation } from "@react-navigation/native"
import * as amplitude from "@amplitude/analytics-react-native"

import { useReferralShareAdapter } from "../../../hooks/useReferralShareAdapter"
import { type ReferralCodePayload } from "../../../types/referralCode"

/** La page n'a pas de cadence : la pastille du cœur est datée par le header, pas par la copie. */
const NO_FREQUENCY = () => {}

export type UseReferralDashboardStateConfig = {
    referralCode: ReferralCodePayload | null
}

export function useReferralDashboardState(config: UseReferralDashboardStateConfig) {
    const { referralCode } = config
    const navigation = useNavigation<{
        canGoBack: () => boolean
        goBack: () => void
        replace: (name: string) => void
    }>()

    const trackCopy = useCallback(() => {
        amplitude.track("referral.link_copied")
    }, [])

    const share = useReferralShareAdapter({
        triggerContext: "referral_dashboard",
        preloadedReferralCode: referralCode,
        markAsShown: NO_FREQUENCY,
        trackCopy,
    })

    /** Legacy `toggleConditions` (`:122-127`) : l'événement part à l'ouverture, pas à la fermeture. */
    const onToggleTerms = useCallback((expanded: boolean) => {
        if (expanded) amplitude.track("referral.conditions_viewed")
    }, [])

    const close = useCallback(() => {
        if (navigation.canGoBack()) navigation.goBack()
        else navigation.replace("menu")
    }, [navigation])

    return {
        shareActions: share.modalProps.shareActions,
        onCopyLink: share.modalProps.onCopyLink,
        shareErrorMessage: share.modalProps.errorMessage,
        onToggleTerms,
        close,
    }
}
